import { useCallback, useEffect, useState } from 'react';
import { clearProjectRole, fetchProjectMembers, setProjectRole } from '../../services/ProjectMemberService';
import { errorMessageOf } from '../../utils/apiErrors';
import { changedRows, rowFor, unreadableRow, type AccessRow } from './projectAccess';
import type { UserRole } from '../../types/User';
import type { Workspace } from '../../utils/workspaces';

export type AccessLoad = { status: 'idle' } | { status: 'loading' } | { status: 'ready' };

export interface AccessSave { saved: number; failed: number; errors: Record<string, string> }

/**
 * One login's access role on each project of the workspace. Each project is read on its own (the routes are per
 * project), so one unreadable project marks its row and leaves the others usable.
 */
export function useProjectAccess(userId: string | null, workspaceRole: UserRole | null, projects: readonly Workspace[], enabled: boolean) {
  const [load, setLoad] = useState<AccessLoad>({ status: 'idle' });
  const [rows, setRows] = useState<AccessRow[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const projectKey = projects.map(p => p.project_id).join(',');

  const reload = useCallback(async (isCurrent: () => boolean = () => true) => {
    if (!enabled || !userId) { setRows([]); setLoad({ status: 'idle' }); return; }
    setLoad({ status: 'loading' });
    const next = await Promise.all(projects.map(async p => {
      try {
        const list = await fetchProjectMembers(p.project_id);
        return rowFor(p.project_id, p.project_name, list.find(m => m.user_id === userId), workspaceRole);
      } catch {
        return unreadableRow(p.project_id, p.project_name, workspaceRole);
      }
    }));
    if (!isCurrent()) return;
    setRows(next);
    setLoad({ status: 'ready' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userId, workspaceRole, projectKey]);

  useEffect(() => {
    let current = true;
    void reload(() => current);
    return () => { current = false; };
  }, [reload]);

  const choose = (projectId: string, value: AccessRow['value']) => {
    setRows(rs => rs.map(r => (r.projectId === projectId ? { ...r, value } : r)));
    setErrors({});
  };

  const dirty = changedRows(rows).length > 0;

  /** Sends every changed row; a failure is kept against its project and never stops the others. */
  const save = async (): Promise<AccessSave> => {
    const out: AccessSave = { saved: 0, failed: 0, errors: {} };
    if (!userId) return out;
    const done = new Set<string>();
    for (const row of changedRows(rows)) {
      try {
        if (row.value === '') await clearProjectRole(row.projectId, userId);
        else await setProjectRole(row.projectId, userId, row.value);
        done.add(row.projectId);
        out.saved += 1;
      } catch (err) {
        out.failed += 1;
        out.errors[row.projectId] = errorMessageOf(err, `Couldn't change access on ${row.projectName}.`);
      }
    }
    // What went through is now the saved value; what failed stays chosen, marked, for another try.
    setRows(rs => rs.map(r => (done.has(r.projectId) ? { ...r, initial: r.value } : r)));
    setErrors(out.errors);
    return out;
  };

  return { load, rows, errors, dirty, choose, save, reload };
}
